"use client"

import Image from "next/image"
import { useLocale, useTranslations } from "next-intl"
import { useRouter, usePathname } from "@/lib/i18n/navigation"
import { Check } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { routing } from "@/lib/i18n/routing"
import { findLegalKey, getLegalSlug, type Locale } from "@/lib/legal/slugs"

const locales = routing.locales

const localeFlags: Record<string, string> = {
  en: "/images/flags/gb.svg",
  de: "/images/flags/de.svg",
  fr: "/images/flags/fr.svg",
}

export function LanguageSwitcher() {
  const locale = useLocale()
  const router = useRouter()
  const pathname = usePathname()
  const t = useTranslations("navigation")

  function switchLocale(newLocale: string) {
    // Legal pages have per-locale slugs, so swapping just the locale prefix
    // would land the user on a 404 (e.g. /de/legal/agb → /fr/legal/agb).
    // Translate the slug to the equivalent in the target locale instead.
    const legalMatch = pathname.match(/^\/legal\/([^/]+)/)
    if (legalMatch) {
      const key = findLegalKey(legalMatch[1])
      if (key) {
        const newSlug = getLegalSlug(key, newLocale as Locale)
        const rewritten = pathname.replace(/^\/legal\/[^/]+/, `/legal/${newSlug}`)
        router.replace(rewritten, { locale: newLocale })
        return
      }
    }

    router.replace(pathname, { locale: newLocale })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <button
            type="button"
            className="inline-flex items-center justify-center min-h-11 min-w-11 ml-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full"
            aria-label={t("switchLanguage")}
          />
        }
      >
        <span
          className="block size-[22px] rounded-full ring-1 ring-border bg-[length:130%] bg-center"
          style={{ backgroundImage: `url(${localeFlags[locale]})` }}
          aria-hidden="true"
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        {locales.map((loc) => (
          <DropdownMenuItem
            key={loc}
            onClick={() => switchLocale(loc)}
            className="flex items-center gap-2.5"
          >
            <Image
              src={localeFlags[loc]}
              alt=""
              width={20}
              height={15}
              className="rounded-[2px]"
              aria-hidden="true"
            />
            <span className="flex-1">{t(`locale.${loc}`)}</span>
            {loc === locale && <Check className="size-4 text-primary" aria-hidden="true" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
